import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { IFeature, IProductType, ISaveView, ISection, IView } from '../../../types';
import type { ICategoryState } from '../../../types/IComponentTypes';
import { IPagination } from '../../../types/IRequestTypes';

import type { IProductCode } from '../../../types/ITypes';
import { CategoryApi } from '../../api/CategoryApi';
import { getViewWithFlag } from '../../services/CategoryHelper';
import { AppState } from '../AppStore';

// It's perform the role to get the masterview from API
export const getViewData = createAsyncThunk(
  'category/getViewData',
  async (params: IProductCode, { dispatch }) => {
    const response = await CategoryApi.getViewDetails(params, dispatch);
    return response;
  }
);

// It's perform the role to get the feature family list from API
export const getFeatureFamilyList = createAsyncThunk(
  'category/getFeatureFamilyList',
  async (params: IProductType, { dispatch }) => {
    const response = await CategoryApi.getFeatureFamilies(params, dispatch);
    return response;
  }
);

// It's perform the role to get the category list from API
export const getCategoryList = createAsyncThunk(
  'category/getCategoryList',
  async (action: IPagination, { dispatch }) => {
    const response = await CategoryApi.getCategories(action, dispatch);
    return response;
  }
);

// It's perform the role to save the master view to the API
export const saveView = createAsyncThunk('category/saveView', async (view: ISaveView, thunkAPI) => {
  const response = await CategoryApi.saveView(view, thunkAPI.dispatch);
  return response;
});

// Define the initial state using that type
const initialValueForSection: ISection = {
  code: '',
  description: '',
  contents: [],
  sections: [],
  hideGeneral: false,
  action: '',
  type: '',
};
const initialState: ICategoryState = {
  view: {
    productCode: '',
    code: '',
    description: '',
    sections: [],
  },
  categories: [],
  featureFamily: [],
  assigned: initialValueForSection,
  unassigned: [],
  showAddCategoryDialog: false,
  sectionForAddSubSection: null,
  parentForAddSubSection: null,
  isCategoryDataModified: false,
};

export const categorySlice = createSlice({
  name: 'category',
  initialState,
  reducers: {
    resetToInitialState: () => {
      return initialState;
    },
    updateView: (state: ICategoryState, action: PayloadAction<IView>) => {
      const { payload } = action;
      state.view = payload;
    },

    showAddCategoryDialog: (
      state: ICategoryState,
      action: PayloadAction<{ section?: ISection; flag?: boolean; parent?: ISection | null }>
    ) => {
      const { payload } = action;
      state.sectionForAddSubSection = payload.section ?? null;
      state.parentForAddSubSection = payload.parent ?? null;
      state.showAddCategoryDialog = payload.flag ?? true;
    },
    addCategory: (state: ICategoryState, action: PayloadAction<Array<ISection>>) => {
      const data = action.payload;
      const { view } = state;
      view.sections = data;
      state.view = view;
      state.isCategoryDataModified = true;
    },
    updateCategoryModify: (state: ICategoryState) => {
      state.isCategoryDataModified = true;
    },
  },
  extraReducers: (builder) => {
    builder
      // GET VIEW DATA
      .addCase(getViewData.fulfilled, (state: ICategoryState, action) => {
        if (action?.meta?.arg?.productCode) {
          const viewData = getViewWithFlag(action.payload.data, action.payload.data.sections[0]);
          if (viewData != null) {
            state.view = viewData;
          }
        } else {
          state.view = initialState.view;
        }

        state.assigned = initialValueForSection;
        state.isCategoryDataModified = false;
      })

      // GET FAMILY FEATURE
      .addCase(getFeatureFamilyList.fulfilled, (state: ICategoryState, action) => {
        if (action.payload.data !== undefined && action.payload.data.length > 0) {
          action.payload.data = action.payload.data.filter(
            (feature: IFeature) => feature.code !== 'PMP'
          );
          state.featureFamily = action.payload.data;
        } else {
          state.featureFamily = [];
        }
      })

      // GET CATEGORY
      .addCase(getCategoryList.fulfilled, (state: ICategoryState, action) => {
        if (action.payload.data !== undefined && action.payload.data.length > 0) {
          state.categories = action.payload.data;
        } else {
          state.categories = [];
        }
      })

      // SAVE CATEGORY
      .addCase(saveView.fulfilled, (state: ICategoryState) => {
        state.isCategoryDataModified = false;
      });
  },
});

// biome-ignore lint/nursery/useExplicitType: Redux Toolkit action creators are inferred
export const {
  updateView,
  showAddCategoryDialog,
  addCategory,
  resetToInitialState,
  updateCategoryModify,
} = categorySlice.actions;

// Other code such as selectors can use the imported `RootState` type
export const selectCategories = (state: AppState): ICategoryState => state.category;

export default categorySlice.reducer;
